import { useTheme } from '@contexts/ThemeContext'
import useFetchChartData from '@hooks/useFetchChartData'
import {
  FetchVenueAnalyticsGraphParams,
  VenueAnalyticsGraphRevenueWeeklySpendTrends,
} from '@models/venue/VenueAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { useAppSelector } from '@store/hooks'
import { selectSelectedVenue } from '@store/VenueReducer'
import { currencyFormat } from '@utils/currencyFormatter'
import { Segmented, theme } from 'antd'
import { useState } from 'react'
import {
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  Text,
  Tooltip,
} from 'recharts'
import BaseChart from '../common/BaseChart'
import CustomTooltip from '../common/CustomTooltip'

const SpendPerDay = () => {
  const { token } = theme.useToken()
  const { mode } = useTheme()
  const venue = useAppSelector(selectSelectedVenue())
  const [selectedSpend, setSelectedSpend] = useState<'reservation' | 'person'>(
    'reservation'
  )

  const { data, isLoading, error } = useFetchChartData<
    FetchVenueAnalyticsGraphParams,
    VenueAnalyticsGraphRevenueWeeklySpendTrends[]
  >({
    entity: 'venue',
    entityId: venue?.id,
    kind: 'revenue-weekly-spend-trends',
    fetchFunction: fetchAnalyticsGraph,
    dependencies: [venue],
  })

  const dataKey =
    selectedSpend === 'reservation' ? 'spendPerReservation' : 'spendPerPerson'

  const CustomTick = (props: any) => {
    const { payload, x, y, index } = props
    if (!data || !data[index]) return null
    const value = data[index][dataKey]
    return (
      <>
        <Text
          x={x}
          y={y}
          textAnchor="middle"
          verticalAnchor="middle"
          fontSize={12}
        >
          {payload.value}
        </Text>
        <Text
          x={x}
          y={y + 15}
          textAnchor="middle"
          verticalAnchor="middle"
          fontSize={12}
          fontWeight="bold"
          fill={mode === 'dark' ? 'white' : 'dark'}
        >
          {currencyFormat(value, true, false)}
        </Text>
      </>
    )
  }

  return (
    <BaseChart
      title="Weekly spend trends"
      data={data}
      isLoading={isLoading}
      error={error}
      actions={
        <Segmented
          value={selectedSpend}
          options={[
            { value: 'reservation', label: 'Per reservation' },
            { value: 'person', label: 'Per person' },
          ]}
          onChange={(e) => setSelectedSpend(e as 'reservation' | 'person')}
        />
      }
    >
      <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
        <PolarGrid />
        <PolarAngleAxis dataKey="name" tick={<CustomTick />} />
        <Tooltip
          content={(props) => (
            <CustomTooltip
              type="radar"
              {...props}
              valueFormatter={(val) => currencyFormat(val, true, false)}
              customLabelFormatter={(payload) => {
                return payload.name
              }}
            />
          )}
        />
        <Radar
          dataKey={dataKey}
          stroke={token.colorPrimary}
          fill={token.colorPrimary}
          fillOpacity={0.4}
        />
      </RadarChart>
    </BaseChart>
  )
}

export default SpendPerDay
